import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Send, MessageSquare, Star, Home, CheckCircle, Loader2, ThumbsUp, ThumbsDown, User, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date to Turkish
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Az önce';
  if (minutes < 60) return `${minutes} dakika önce`;
  if (hours < 24) return `${hours} saat önce`;
  if (days < 7) return `${days} gün önce`;
  
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Single Feedback Card
const FeedbackCard = ({ feedback, onVote }) => {
  const [voting, setVoting] = useState(false);
  const [voted, setVoted] = useState(null);
  
  const handleVote = async (type) => {
    if (voted) return;
    setVoting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/feedback/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback_id: feedback.feedbackId,
          vote_type: type
        })
      });
      
      if (response.ok) {
        setVoted(type);
        onVote(feedback.feedbackId, type);
      }
    } catch (err) {
      console.error('Vote error:', err);
    } finally {
      setVoting(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-panel p-5 space-y-4"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500/30 to-cyan-500/30 flex items-center justify-center border border-violet-500/30">
            <User className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h4 className="text-white font-medium">{feedback.nickname}</h4>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3 h-3" />
              {formatDate(feedback.createdAt)}
            </div>
          </div>
        </div>
        
        {/* Star Rating */}
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-4 h-4 ${
                star <= feedback.rating 
                  ? 'text-amber-400 fill-amber-400' 
                  : 'text-slate-600'
              }`}
            />
          ))}
        </div>
      </div>
      
      {/* Content */}
      <p className="text-slate-300 text-sm leading-relaxed">
        {feedback.feedback}
      </p>
      
      {/* Vote Buttons */}
      <div className="flex items-center gap-4 pt-2 border-t border-slate-800">
        <button
          onClick={() => handleVote('like')}
          disabled={voting || voted}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
            voted === 'like'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'text-slate-400 hover:text-green-400 hover:bg-green-500/10'
          } ${voted && voted !== 'like' ? 'opacity-50' : ''}`}
        >
          <ThumbsUp className="w-4 h-4" />
          <span>{feedback.likes + (voted === 'like' ? 1 : 0)}</span>
        </button>
        
        <button
          onClick={() => handleVote('dislike')}
          disabled={voting || voted}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
            voted === 'dislike'
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'text-slate-400 hover:text-red-400 hover:bg-red-500/10'
          } ${voted && voted !== 'dislike' ? 'opacity-50' : ''}`}
        >
          <ThumbsDown className="w-4 h-4" />
          <span>{feedback.dislikes + (voted === 'dislike' ? 1 : 0)}</span>
        </button>
      </div>
    </motion.div>
  );
};

// Feedback List Component
const FeedbackList = ({ feedbacks, onVote }) => {
  if (feedbacks.length === 0) {
    return (
      <div className="text-center py-10 text-slate-500">
        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Henüz geri bildirim yok. İlk siz yazın!</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {feedbacks.map((fb, index) => (
        <FeedbackCard key={fb.feedbackId || index} feedback={fb} onVote={onVote} />
      ))}
    </div>
  );
};

export const FeedbackPage = ({ onBack, onHome }) => {
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [nickname, setNickname] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'write'
  
  // Load feedbacks
  useEffect(() => {
    const loadFeedbacks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/feedbacks`);
        if (response.ok) {
          const data = await response.json();
          setFeedbacks(data.feedbacks || []);
        }
      } catch (err) {
        console.error('Load feedbacks error:', err);
      } finally {
        setLoadingFeedbacks(false);
      }
    };
    
    loadFeedbacks();
  }, [submitted]);
  
  const handleVote = (feedbackId, voteType) => {
    setFeedbacks(prev => prev.map(fb => {
      if (fb.feedbackId === feedbackId) {
        return {
          ...fb,
          likes: fb.likes + (voteType === 'like' ? 1 : 0),
          dislikes: fb.dislikes + (voteType === 'dislike' ? 1 : 0)
        };
      }
      return fb;
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rating,
          feedback,
          nickname: nickname || 'Anonim'
        })
      });
      
      if (!response.ok) {
        throw new Error('Gönderim başarısız');
      }
      
      setSubmitted(true);
      setActiveTab('list');
      
      // Reset form
      setFeedback('');
      setRating(0);
      setNickname('');
      
      // Reload feedbacks
      setTimeout(() => setSubmitted(false), 100);
      
    } catch (err) {
      setError('Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.');
      console.error('Feedback error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <motion.div
      className="min-h-screen p-6 pb-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <button
        onClick={onBack}
        className="fixed top-6 left-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      
      <button
        onClick={onHome}
        className="fixed top-6 right-6 z-40 glass-panel p-3 text-slate-400 hover:text-white transition-all flex items-center gap-2"
        data-testid="home-button"
      >
        <Home className="w-5 h-5" />
        <span className="text-sm">Home</span>
      </button>
      
      <div className="max-w-2xl mx-auto pt-16 space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          <motion.div
            className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center border border-violet-500/30"
          >
            <MessageSquare className="w-10 h-10 text-violet-400" />
          </motion.div>
          
          <h1 className="text-3xl font-heading text-slate-100">
            Geri Bildirimler
          </h1>
          <p className="text-slate-400 text-sm">
            Kullanıcı yorumlarını görüntüleyin veya kendi düşüncelerinizi paylaşın
          </p>
        </div>
        
        {/* Tab Buttons */}
        <div className="flex gap-2 p-1 bg-slate-800/50 rounded-full">
          <button
            onClick={() => setActiveTab('list')}
            className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all ${
              activeTab === 'list'
                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-list"
          >
            Yorumlar ({feedbacks.length})
          </button>
          <button
            onClick={() => setActiveTab('write')}
            className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all ${
              activeTab === 'write'
                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-write"
          >
            Yorum Yaz
          </button>
        </div>
        
        <AnimatePresence mode="wait">
          {activeTab === 'list' ? (
            <motion.div
              key="list"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {loadingFeedbacks ? (
                <div className="text-center py-10">
                  <Loader2 className="w-8 h-8 mx-auto animate-spin text-violet-400" />
                  <p className="text-slate-500 mt-2">Yükleniyor...</p>
                </div>
              ) : (
                <FeedbackList feedbacks={feedbacks} onVote={handleVote} />
              )}
            </motion.div>
          ) : (
            <motion.div
              key="write"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Nickname */}
                <div className="glass-panel p-5 space-y-3">
                  <label className="text-sm text-slate-300 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Takma Adınız
                  </label>
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Görünecek isminiz..."
                    maxLength={30}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500"
                    data-testid="nickname-input"
                  />
                </div>
                
                {/* Rating */}
                <div className="glass-panel p-5 space-y-3">
                  <label className="text-sm text-slate-300">Puanınız</label>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className={`p-2 transition-all ${
                          star <= rating ? 'text-amber-400 scale-110' : 'text-slate-600 hover:text-slate-400'
                        }`}
                        data-testid={`star-${star}`}
                      >
                        <Star className={`w-10 h-10 ${star <= rating ? 'fill-amber-400' : ''}`} />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Feedback text */}
                <div className="glass-panel p-5 space-y-3">
                  <label className="text-sm text-slate-300">Yorumunuz</label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Uygulama hakkında düşüncelerinizi yazın..."
                    className="w-full h-32 bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500 resize-none"
                    required
                    data-testid="feedback-textarea"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={!feedback || rating === 0 || loading}
                  className="w-full rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 px-8 py-4 text-sm font-medium text-white transition-all hover:from-violet-500/30 hover:to-cyan-500/30 border border-violet-500/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Yayınla
                    </>
                  )}
                </button>
                
                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default FeedbackPage;
